import { Place } from 'models/beneficiaries';
import { useState, useEffect, useCallback } from 'react';
import debounce from 'lodash/debounce';
import useAlphaSnackbar from 'hooks/useAlphaSnackbar';
import t from 'utils/translationHelper';

const useAddressQuery = () => {
  const [places, setPlaces] = useState<Place[]>([]);
  const [textQuery, setTextQuery] = useState<string>();
  const [selectedPlace, setSelectedPlace] = useState<Place>();
  const sb = useAlphaSnackbar();

  const [addressLoading, setAddressLoading] = useState<boolean>(false);
  const fetchPlacesDebounced = useCallback(
    debounce((query: string) => {
      setAddressLoading(true);
        if(query) {
          searchPlaces(query);
        }
    }, 400),
    []
  );

  const searchPlaces = async (textQuery: string) => {
  // check window.google.maps is defined
  if(!window?.google?.maps) {
    sb.trigger(t('error_searching_address'), 'error');
    return;
  }

    const { Place } = (await google.maps.importLibrary(
      'places'
    )) as google.maps.PlacesLibrary;
   
    // Reference: https://developers.google.com/maps/documentation/javascript/reference/place#SearchByTextRequest
    const request = {
      textQuery: textQuery,
      fields: ['displayName', 'addressComponents', 'formattedAddress'],
      includedType: '', // Restrict query to a specific type (leave blank for any).
      maxResultCount: 20,
    };
    Place.searchByText(request).then((response) => {
      const placesToDisplay = response.places?.map((place) => {
        return {
          displayName: { text: place.displayName, languageCode: '' },
          formattedAddress: place.formattedAddress ?? '-',
          addressComponents: place.addressComponents ?? [],
        }
      })
      setPlaces(placesToDisplay as unknown as Place[]);
    }).catch((error) => {
      console.error(error);
      sb.trigger(t('error_searching_address'), 'error');
    }).finally(() => {
      setAddressLoading(false);
    });
  };
  useEffect(() => {
    if (textQuery && textQuery.length >= 3) { 
      setAddressLoading(true);
      fetchPlacesDebounced(textQuery);
    } else {
      setPlaces([]);
    }
    return () => {
      fetchPlacesDebounced.cancel();
    };
  }, [textQuery, fetchPlacesDebounced]);

  return {
    places,
    textQuery,
    setTextQuery,
    selectedPlace,
    setSelectedPlace,
    addressLoading,
  };
};

export default useAddressQuery;
